import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export default function Register() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showPass, setShowPass] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = 'Name must be at least 2 characters.';
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = 'Please enter a valid email.';
    if (!form.password || form.password.length < 6) errs.password = 'Password must be at least 6 characters.';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/register`, form);
      login(data.user, data.token);
      toast.success(`Account created! Welcome, ${data.user.name.split(' ')[0]}! 🎉`);
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Registration failed.');
    } finally {
      setLoading(false);
    }
  };

  const strength = form.password.length >= 10 ? 'Strong' : form.password.length >= 6 ? 'Good' : form.password.length > 0 ? 'Weak' : '';
  const strengthColor = strength === 'Strong' ? 'text-green-600' : strength === 'Good' ? 'text-yellow-600' : 'text-red-500';
  const strengthWidth = strength === 'Strong' ? 'w-full' : strength === 'Good' ? 'w-2/3' : strength === 'Weak' ? 'w-1/3' : 'w-0';
  const strengthBg = strength === 'Strong' ? 'bg-green-500' : strength === 'Good' ? 'bg-yellow-400' : 'bg-red-400';

  return (
    <main className="min-h-screen bg-gradient-to-br from-brand-50 via-white to-purple-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-md animate-slide-up">

        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-brand-600 font-bold text-2xl mb-2">
            🔗 BitLinks
          </div>
          <h1 className="text-2xl font-black text-gray-900">Create your account</h1>
          <p className="text-gray-500 text-sm mt-1">Free forever. No credit card needed.</p>
        </div>

        <div className="card shadow-lg border-0">
          <form onSubmit={handleSubmit} className="flex flex-col gap-5">

            {/* Name */}
            <div>
              <label className="label">Full Name</label>
              <input
                type="text"
                className={`input ${errors.name ? 'border-red-400 ring-2 ring-red-200' : ''}`}
                placeholder="Rahul Sharma"
                value={form.name}
                onChange={e => { setForm({ ...form, name: e.target.value }); setErrors({ ...errors, name: '' }); }}
                autoComplete="name"
              />
              {errors.name && <p className="mt-1.5 text-xs text-red-500">⚠ {errors.name}</p>}
            </div>

            {/* Email */}
            <div>
              <label className="label">Email</label>
              <input
                type="email"
                className={`input ${errors.email ? 'border-red-400 ring-2 ring-red-200' : ''}`}
                placeholder="you@example.com"
                value={form.email}
                onChange={e => { setForm({ ...form, email: e.target.value }); setErrors({ ...errors, email: '' }); }}
                autoComplete="email"
              />
              {errors.email && <p className="mt-1.5 text-xs text-red-500">⚠ {errors.name}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="label">Password</label>
              <div className="relative">
                <input
                  type={showPass ? 'text' : 'password'}
                  className={`input pr-12 ${errors.password ? 'border-red-400 ring-2 ring-red-200' : ''}`}
                  placeholder="Min. 6 characters"
                  value={form.password}
                  onChange={e => { setForm({ ...form, password: e.target.value }); setErrors({ ...errors, password: '' }); }}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  onClick={() => setShowPass(!showPass)}
                >
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                </button>
              </div>
              {/* Password strength */}
              {form.password.length > 0 && (
                <div className="mt-2">
                  <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full transition-all duration-300 ${strengthWidth} ${strengthBg}`} />
                  </div>
                  <p className={`text-xs mt-1 font-medium ${strengthColor}`}>{strength}</p>
                </div>
              )}
              {errors.password && <p className="mt-1.5 text-xs text-red-500">⚠ {errors.password}</p>}
            </div>

            <button type="submit" className="btn-primary w-full py-3 text-base mt-1" disabled={loading}>
              {loading ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
                  </svg>
                  Creating account...
                </>
              ) : 'Create Account →'}
            </button>

            <p className="text-xs text-center text-gray-400">
              By creating an account, you agree to our Terms of Service.
            </p>
          </form>
        </div>

        <p className="text-center text-sm text-gray-500 mt-5">
          Already have an account?{' '}
          <Link to="/login" className="text-brand-600 font-semibold hover:underline">Sign in</Link>
        </p>
      </div>
    </main>
  );
}
